import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ms from "@sprout/client-lib/dist/ms";
import styled, { css } from "styled-components";
import { Editor as BaseEditor, useEditorRef } from "sprout-ui-dom";
import { isDraftJSEditorRawState, formatToTipTapJSON } from "sprout-lib/dist/parser";
import parseDraftJSRaw from "sprout-lib/dist/parser/parse/draftjs";
import { debounce } from "lodash";
import { usePostPage_UpdateItemMutation } from "sprout-graphql";
import { useAfterMountEffect } from "sprout-hooks";
import { FocusFrom, usePostPageFocus } from "../PostPageFocusContext";
import { usePostPageViewMode } from "../PostPageViewModeContext";
import useFocusNoteEditor from "./useFocusNoteEditor";
const PostPageNoteEditorWrapper = styled("div")`
  ${({ $useMinHeight  })=>$useMinHeight && css`
      .ProseMirror {
        min-height: 200px;
      }
    `}
`;
const Editor = styled(BaseEditor)`
  font-size: 17px;
`;
function getInitialState(note) {
    if (!note) return undefined;
    try {
        const state = JSON.parse(note);
        if (isDraftJSEditorRawState(state)) return formatToTipTapJSON(parseDraftJSRaw(state, true).text).json;
        else return state;
    } catch (e) {
        return undefined;
    }
}
const PostPageNoteEditor = (props)=>{
    const { note , setIsNoteBlank , itemId , onSave  } = props;
    const [mode] = usePostPageViewMode();
    const [focusFrom, setFocusFrom, focusFromRef] = usePostPageFocus();
    const focusNote = useFocusNoteEditor();
    const [updatePost] = usePostPage_UpdateItemMutation();
    const save = useMemo(()=>debounce((noteToSave, itemIdToSave)=>{
            updatePost({
                variables: {
                    itemId: itemIdToSave,
                    note: noteToSave,
                    includeNote: true,
                    includeDescription: false,
                    includeUrl: false
                }
            }).catch((error)=>{
                console.error(error);
            });
        }, ms.seconds(1)), [
        updatePost
    ]);
    const handleChange = useCallback(({ getStateAsJson , isEmpty  })=>{
        const json = getStateAsJson();
        const empty = isEmpty();
        setIsNoteBlank(empty);
        save(empty ? null : JSON.stringify(json), itemId);
    }, [
        itemId,
        save,
        setIsNoteBlank
    ]);
    const editorRef = useEditorRef();
    const unmountRef = useRef(false);
    const [secondaryFocusHack, setSecondaryFocusHack] = useState(false);
    /*
   * This is needed to prevent memory leak in `handleBlur` when it calls
   * `setSecondaryFocusHack` because the editor can potentially be unmount
   */ useEffect(()=>{
        return ()=>{
            unmountRef.current = true;
        };
    }, []);
    const handleBlur = useCallback(()=>{
        if (focusFromRef.current !== FocusFrom.NOTE) {
            setFocusFrom(null);
            if (!unmountRef.current) setSecondaryFocusHack(false);
        }
    }, [
        focusFromRef,
        setFocusFrom
    ]);
    const handleFocus = useCallback(()=>{
        focusNote();
    }, [
        focusNote
    ]);
    const handleEscape = useCallback(({ blur  })=>{
        blur();
        return true;
    }, []);
    useEffect(()=>{
        if (focusFrom === FocusFrom.NOTE) setSecondaryFocusHack(true);
    }, [
        focusFrom
    ]);
    useEffect(()=>{
        if (secondaryFocusHack && editorRef.current && !editorRef.current.isFocused()) editorRef.current.focus();
    }, [
        editorRef,
        secondaryFocusHack
    ]);
    // This is required for `PostPage` to `PostPage` transitions
    useAfterMountEffect(()=>{
        var ref;
        (ref = editorRef.current) === null || ref === void 0 ? void 0 : ref.setState(getInitialState(note) || null);
        return ()=>{
            save.flush();
        };
    }, [
        itemId
    ]);
    return /*#__PURE__*/ _jsx(PostPageNoteEditorWrapper, {
        $useMinHeight: focusFrom === FocusFrom.NOTE,
        children: /*#__PURE__*/ _jsx(Editor, {
            initialState: getInitialState(note),
            readOnly: mode === "VIEW",
            mode: "FULL",
            placeholder: "Add a note...",
            onEscape: handleEscape,
            onChange: handleChange,
            onBlur: handleBlur,
            onFocus: handleFocus,
            onSave: onSave,
            ref: editorRef
        })
    });
};
export default PostPageNoteEditor;
